import {useState} from 'react';
import {Link} from 'react-router-dom';
import {trpc} from '@common-web/lib/trpc';
import {Settings, Plus, FileText, MoreHorizontal, Pencil, Trash2, Copy, Sparkles} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {PageHeader, PageHeaderAction} from '@common-web/components/page-header';
import {Card, CardContent} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@common-web/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@common-web/components/ui/dialog';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {Textarea} from '@common-web/components/ui/textarea';

function formatDate(date: Date | string): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function OnboardingTemplateList() {
  const [selectedClientId, setSelectedClientId] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');

  const clientsQuery = trpc.internal.client.list.useQuery();
  const utils = trpc.useUtils();

  const templatesQuery = trpc.internal.onboarding.listTemplates.useQuery(
    {clientId: selectedClientId},
    {enabled: !!selectedClientId},
  );

  const createMutation = trpc.internal.onboarding.createTemplate.useMutation({
    onSuccess: () => {
      utils.internal.onboarding.listTemplates.invalidate();
      setShowCreateDialog(false);
      setNewTemplateName('');
      setNewTemplateDescription('');
    },
  });

  const seedDefaultMutation = trpc.internal.onboarding.seedDefaultTemplate.useMutation({
    onSuccess: () => {
      utils.internal.onboarding.listTemplates.invalidate();
    },
  });

  const duplicateMutation = trpc.internal.onboarding.duplicateTemplate.useMutation({
    onSuccess: () => {
      utils.internal.onboarding.listTemplates.invalidate();
    },
  });

  const hasDefault = (templatesQuery.data ?? []).some((t) => t.isDefault);

  function handleCreateTemplate() {
    if (!newTemplateName.trim() || !selectedClientId) return;
    createMutation.mutate({
      clientId: selectedClientId,
      name: newTemplateName.trim(),
      description: newTemplateDescription.trim() || undefined,
      steps: [],
    });
  }

  function handleSeedDefault() {
    if (!selectedClientId) return;
    seedDefaultMutation.mutate({clientId: selectedClientId});
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        {/* Header */}
        <PageHeader
          title='Onboarding Templates'
          description='Configure onboarding step templates per client'
          icon={<Settings className='h-6 w-6' />}
          isFetching={templatesQuery.isFetching}
        >
          <PageHeaderAction
            tone='secondary'
            onClick={handleSeedDefault}
            disabled={!selectedClientId || hasDefault || seedDefaultMutation.isPending}
            title={
              hasDefault
                ? 'This client already has a default template'
                : 'Create the seeded default onboarding template'
            }
          >
            <Sparkles className='h-4 w-4 mr-2' />
            {seedDefaultMutation.isPending ? 'Creating...' : 'Create Default Template'}
          </PageHeaderAction>
          <PageHeaderAction onClick={() => setShowCreateDialog(true)} disabled={!selectedClientId}>
            <Plus className='h-4 w-4 mr-2' />
            New Template
          </PageHeaderAction>
        </PageHeader>

        {/* Main Content */}
        <div className='px-8 py-6 max-w-7xl mx-auto w-full'>
          {/* Client selector */}
          <div className='flex items-center gap-3 mb-6'>
            <Label className='text-sm font-medium text-brand-gray'>Client</Label>
            <Select
              value={selectedClientId || 'placeholder'}
              onValueChange={(val) => setSelectedClientId(val === 'placeholder' ? '' : val)}
            >
              <SelectTrigger className='w-64'>
                <SelectValue placeholder='Select a client' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='placeholder' disabled>
                  Select a client
                </SelectItem>
                {(clientsQuery.data ?? []).map((client: {id: string; name: string}) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Templates list */}
          {!selectedClientId ? (
            <div className='text-center py-16 text-brand-gray-light'>
              <Settings className='h-12 w-12 mx-auto mb-3 opacity-40' />
              <p className='text-sm'>Select a client to view their onboarding templates.</p>
            </div>
          ) : templatesQuery.isLoading ? (
            <div className='space-y-3'>
              {[1, 2, 3].map((i) => (
                <div key={i} className='h-20 bg-gray-100 rounded-lg animate-pulse' />
              ))}
            </div>
          ) : (templatesQuery.data ?? []).length === 0 ? (
            <div className='text-center py-16 text-brand-gray-light'>
              <FileText className='h-12 w-12 mx-auto mb-3 opacity-40' />
              <p className='text-sm'>No templates found for this client.</p>
              <p className='text-xs mt-1'>Create one to get started.</p>
              <Button
                className='mt-4'
                onClick={handleSeedDefault}
                disabled={seedDefaultMutation.isPending}
              >
                <Sparkles className='h-4 w-4 mr-2' />
                {seedDefaultMutation.isPending ? 'Creating...' : 'Create Default Template'}
              </Button>
              {seedDefaultMutation.error && (
                <p className='text-xs text-red-500 mt-3'>{seedDefaultMutation.error.message}</p>
              )}
            </div>
          ) : (
            <div className='space-y-3'>
              {(templatesQuery.data ?? []).map(
                (template: {
                  id: string;
                  name: string;
                  description: string | null;
                  isDefault: boolean;
                  stepCount: number;
                  createdAt: Date | string;
                  updatedAt: Date | string;
                }) => (
                  <Card key={template.id} className='hover:shadow-md transition-shadow'>
                    <CardContent className='p-4'>
                      <div className='flex items-center justify-between'>
                        <div className='flex items-center gap-3'>
                          <div className='h-10 w-10 rounded-lg bg-brand-blue/10 flex items-center justify-center'>
                            <FileText className='h-5 w-5 text-brand-blue' />
                          </div>
                          <div>
                            <div className='flex items-center gap-2'>
                              <Link
                                to={`/onboarding/templates/${template.id}`}
                                className='text-sm font-semibold text-brand-navy hover:text-brand-blue transition-colors'
                              >
                                {template.name}
                              </Link>
                              {template.isDefault && (
                                <Badge className='bg-emerald/10 text-emerald text-xs'>
                                  Default
                                </Badge>
                              )}
                            </div>
                            {template.description && (
                              <p className='text-xs text-brand-gray mt-0.5'>
                                {template.description}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className='flex items-center gap-6'>
                          <div className='text-right'>
                            <p className='text-sm font-medium text-brand-navy'>
                              {template.stepCount} steps
                            </p>
                            <p className='text-xs text-brand-gray'>
                              Updated {formatDate(template.updatedAt)}
                            </p>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant='ghost' size='icon' className='h-8 w-8'>
                                <MoreHorizontal className='h-4 w-4' />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align='end'>
                              <DropdownMenuItem asChild>
                                <Link to={`/onboarding/templates/${template.id}`}>
                                  <Pencil className='h-3.5 w-3.5 mr-2' />
                                  Edit
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={duplicateMutation.isPending}
                                onSelect={() =>
                                  duplicateMutation.mutate({templateId: template.id})
                                }
                              >
                                <Copy className='h-3.5 w-3.5 mr-2' />
                                Duplicate
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ),
              )}
            </div>
          )}

          {/* Create Template Dialog */}
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Template</DialogTitle>
              </DialogHeader>
              <div className='space-y-4'>
                <div className='space-y-2'>
                  <Label htmlFor='templateName'>Template Name</Label>
                  <Input
                    id='templateName'
                    value={newTemplateName}
                    onChange={(e) => setNewTemplateName(e.target.value)}
                    placeholder='e.g., Standard Onboarding'
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='templateDescription'>Description (optional)</Label>
                  <Textarea
                    id='templateDescription'
                    value={newTemplateDescription}
                    onChange={(e) => setNewTemplateDescription(e.target.value)}
                    placeholder='Describe what this template is for...'
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant='outline' onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateTemplate}
                  disabled={!newTemplateName.trim() || createMutation.isPending}
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Template'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
